import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, Compass, ListChecks, ClipboardCheck, FileText, LogOut, Menu, X, Footprints, FileCog, Signpost, UserRound, Puzzle, Gem, Users, Brain, Building2 } from "lucide-react";
import { useState } from "react";

const DASH = { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard };
const LATIHAN = { to: "/latihan-otak", label: "Latihan Otak", icon: Puzzle };
const STUDENT_NAV = [
  { to: "/asesmen", label: "Asesmen", icon: ClipboardCheck },
  { to: "/profilku", label: "Profilku", icon: UserRound },
  { to: "/jelajahi-bidang", label: "Jelajahi Bidang", icon: Compass },
  { to: "/susun-langkah", label: "Susun Langkah", icon: ListChecks },
  { to: "/tryout", label: "TryOut", icon: FileText },
  { to: "/asesmen?full=1", label: "Asesmen Pro", icon: Gem },
  { to: "/info-jalur", label: "Info Jalur", icon: Signpost },
  LATIHAN,
];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = () => { logout(); nav("/"); };
  const navItems = user?.role === "orangtua"
    ? [DASH, LATIHAN]
    : user?.role === "admin"
    ? [DASH, { to: "/admin/soal", label: "Kelola Soal", icon: FileCog }, { to: "/admin/tryout-upload", label: "Upload TryOut", icon: FileText }, { to: "/admin/asesmen-upload", label: "Upload Asesmen", icon: Brain }, { to: "/admin/latihan-upload", label: "Recognize/Postes", icon: FileText }, { to: "/admin/kelola-upload", label: "Kelola Upload", icon: FileText }, { to: "/admin/laporan-bulanan", label: "Laporan Bulanan", icon: FileText }, { to: "/admin/profil-sekolah", label: "Profil Sekolah", icon: Building2 }, { to: "/admin/simulasi", label: "Hasil Simulasi", icon: FileText }, { to: "/admin/akun", label: "Kelola Akun", icon: Users }]
    : STUDENT_NAV;

  return (
    <div className="min-h-screen bg-[#FAF8F5] grain">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#FAF8F5]/80 border-b border-[#E5E0D8]">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2" data-testid="brand-logo">
            <img src="/talenta-logo.png" alt="Talenta" data-testid="talenta-logo" className="h-10 w-10 rounded-full object-contain shrink-0" />
            <span className="flex flex-col leading-none">
              <span className="font-heading font-black text-2xl md:text-3xl"><span className="text-[#E25E3E]">Talenta</span><span className="text-[#F4B942]">Path</span></span>
              <span className="text-[10px] font-medium text-[#9C9A94] self-end -mt-1">powered by Talenta</span>
              <span className="text-[9px] italic text-[#9C9A94] self-end -mt-0.5">Your Talent is Our Concern</span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((n) => {
              const active = loc.pathname === n.to;
              return (
                <Link key={n.to} to={n.to} data-testid={`nav-${n.to.slice(1)}`}
                  className={`px-3 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-1.5 ${active ? "bg-[#1A1C1A] text-white" : "text-[#5C5F5C] hover:bg-[#1A1C1A]/5"}`}>
                  <n.icon className="h-4 w-4" /> {n.label}
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-2">
            <div className="hidden sm:flex flex-col items-end leading-tight">
              <span className="text-sm font-semibold text-[#1A1C1A]">{user?.name}</span>
              <span className="text-xs text-[#5C5F5C] capitalize">{user?.role}{user?.kelas ? ` · Kelas ${user.kelas}` : ""}</span>
            </div>
            <button onClick={doLogout} data-testid="logout-btn" className="p-2 rounded-full text-[#5C5F5C] hover:bg-[#1A1C1A]/5 transition-colors" title="Keluar">
              <LogOut className="h-5 w-5" />
            </button>
            <button onClick={() => setOpen(!open)} className="md:hidden p-2 rounded-full hover:bg-[#1A1C1A]/5" data-testid="mobile-menu-btn">
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
        {open && (
          <div className="md:hidden border-t border-[#E5E0D8] bg-[#FAF8F5] px-4 py-2 space-y-1">
            {navItems.map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setOpen(false)}
                className="flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium text-[#1A1C1A] hover:bg-[#1A1C1A]/5">
                <n.icon className="h-4 w-4" /> {n.label}
              </Link>
            ))}
          </div>
        )}
      </header>
      <main className="max-w-6xl mx-auto px-4 py-8">{children}</main>
    </div>
  );
}
